import { ICampaignRecipient } from '@dataTypes/marketing.type'
import axios from '../axiosConfig'
import { getEnv } from '@utils/env'
import { logger } from '@utils/safeLogger'
import { toApiError } from '../errors/api-error'

// Constants
const BUSINESS_LAYER_API = getEnv('REACT_APP_BUSINESS_LAYER_MARKETING', '')

const headers = {
  'Content-Type': 'application/json',
}

// Types
interface ApiResponse<T> {
  statusCode: number
  message: string
  data: T
}

type CampaignsRecipientData = {
  campaign_recipients?: ICampaignRecipient[]
}

type GenericResponseData = Record<string, unknown>

type UpdateCampaignRecipientResponse = ApiResponse<CampaignsRecipientData>
type CreateCampaignRecipientResponse = ApiResponse<GenericResponseData>

// API Functions
export const updateCampaignRecipientById = async (
  campaign_recipient_id: string,
  updatedData: Partial<ICampaignRecipient>,
): Promise<UpdateCampaignRecipientResponse> => {
  try {
    const response = await axios.put<UpdateCampaignRecipientResponse>(
      `${BUSINESS_LAYER_API}/api/v1/campaign/recipient`,
      { campaign_recipient_id, data: updatedData },
      {
        headers,
      },
    )

    return response.data
  } catch (e: unknown) {
    logger.error('Error in updateCampaignRecipientById:', e)
    throw toApiError(e, 'An unknown error occurred')
  }
}

export const createCampaignRecipient = async (
  recipientDetails: ICampaignRecipient,
): Promise<CreateCampaignRecipientResponse> => {
  try {
    const response = await axios.post<CreateCampaignRecipientResponse>(
      `${BUSINESS_LAYER_API}/api/v1/campaign/recipient`,
      recipientDetails,
      {
        headers,
      },
    )

    return response.data
  } catch (e: unknown) {
    logger.error('Error in createCampaignRecipient:', e)
    throw toApiError(e, 'An unknown error occurred')
  }
}

export const createMultipleCampaignRecipients = async (
  recipients: ICampaignRecipient[],
): Promise<CreateCampaignRecipientResponse> => {
  try {
    const response = await axios.post<CreateCampaignRecipientResponse>(
      `${BUSINESS_LAYER_API}/api/v1/campaign/recipient/bulk`,
      { campaign_recipients: recipients },
      {
        headers,
      },
    )

    return response.data
  } catch (e: unknown) {
    logger.error('Error in createMultipleCampaignRecipients:', e)
    throw toApiError(e, 'An unknown error occurred')
  }
}

export const deleteCampaignRecipientById = async (
  campaignRecipientId: string,
): Promise<CreateCampaignRecipientResponse> => {
  try {
    const response = await axios.delete<CreateCampaignRecipientResponse>(
      `${BUSINESS_LAYER_API}/api/v1/campaign/recipient/${campaignRecipientId}`,
      {
        headers,
      },
    )

    return response.data
  } catch (e: unknown) {
    logger.error('Error in deleteCampaignRecipientById:', e)
    throw toApiError(e, 'An unknown error occurred')
  }
}

export const deleteCampaignRecipientByContactId = async (
  contactId: string,
): Promise<CreateCampaignRecipientResponse> => {
  try {
    const response = await axios.delete<CreateCampaignRecipientResponse>(
      `${BUSINESS_LAYER_API}/api/v1/campaign/recipient/contact/${contactId}`,
      {
        headers,
      },
    )

    return response.data
  } catch (e: unknown) {
    logger.error('Error in deleteCampaignRecipientByContactId:', e)
    throw toApiError(e, 'An unknown error occurred')
  }
}

export const deleteMultipleCampaignRecipients = async (
  campaignRecipientIds: string[],
): Promise<CreateCampaignRecipientResponse> => {
  try {
    const response = await axios.delete<CreateCampaignRecipientResponse>(
      `${BUSINESS_LAYER_API}/api/v1/campaign/recipient/bulk`,
      {
        headers,
        data: {
          campaign_recipient_ids: campaignRecipientIds,
        },
      },
    )

    return response.data
  } catch (e: unknown) {
    logger.error('Error in deleteMultipleCampaignRecipients:', e)
    throw toApiError(e, 'An unknown error occurred')
  }
}
